(ns minorodata.components.help.shortcuts-modal
  (:require ["lucide-react" :refer [BrainCircuit Cable GitGraph Group LayoutDashboardIcon LayoutList
                                    ListChecks Share2 SquareStack Warehouse]]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.app.posthog-events :as posthog-events]
            [re-frame.core :as re-frame]
            [ui.typography :as typography]
            [ui.modal]
            [ui.shortcut]
            [react-hotkeys-hook :refer [useHotkeys]]))


(def modal-id "help-shortcuts")

(defn- navigate-shortcut [shortcut route]
  #(dispatch [:keyboard-shortcuts.events/navigate shortcut route]))

(defn shortcut [description keys & [icon]]
  [:li {:class "flex flex-row items-center w-full px-4 py-2 gap-4"}
   [:div {:class "grow min-w-0 flex flex-row items-center gap-2"}
    (when icon
      [:> icon {:size 16 :class "shrink-0 opacity-75"}])
    description]
   [:div {:class "shrink-0"}
    [ui.shortcut/shortcut {:keys    keys
                           :keycap? true
                           :class   "opacity-100"}]]])

(defn modal []
  (let [open?  @(re-frame/subscribe [:modals.subs/modal-open? modal-id])

        toggle-modal-fn (fn toggle-modal []
                        (dispatch [:modals.events/toggle-modal modal-id (not open?)])
                        (when-not open?
                          (dispatch [:posthog/capture {:event (:keyboard-shortcuts/modal-opened posthog-events/events)
                                                       :props {:trigger "keyboard"}}])))

        ;; Same flags and permissions the sidebar uses for these sections.
        ;; Wrapped in boolean because useHotkeys only treats `false` as disabled.
        overview?         @(re-frame/subscribe [:unleash/is-enabled "fe.dashboard"])
        pipeline-editor?  (boolean (and @(re-frame/subscribe [:unleash/is-enabled "fe.pipeline-editor"])
                                        @(re-frame/subscribe [:users.subs/has-permissions? #{:pipeline-editor/read}])))
        analytics-models? (boolean (and @(re-frame/subscribe [:unleash/is-enabled "fe.analytics-models"])
                                        @(re-frame/subscribe [:users.subs/has-permissions? #{:analytics-models/read}])))
        warehouse-usage?  (boolean (and @(re-frame/subscribe [:unleash/is-enabled "cost-section"])
                                        @(re-frame/subscribe [:app.subs/enabled-for-bigquery :costs])
                                        @(re-frame/subscribe [:users.subs/has-permissions? #{:warehouse-health/read}])))]

    (useHotkeys "?" toggle-modal-fn #js {:useKey true})

    (useHotkeys "g>t" (navigate-shortcut "g>t" :transformations))
    (useHotkeys "g>s" (navigate-shortcut "g>s" :extracts))
    (useHotkeys "g>p" (navigate-shortcut "g>p" :dag))
    (useHotkeys "g>l" (navigate-shortcut "g>l" :log))
    (useHotkeys "g>q" (navigate-shortcut "g>q" :transform-queue))
    (useHotkeys "g>d" (navigate-shortcut "g>d" :data-views))
    (useHotkeys "g>o" (navigate-shortcut "g>o" :overview) #js {:enabled overview?})
    (useHotkeys "g>e" (navigate-shortcut "g>e" :pipeline-editor) #js {:enabled pipeline-editor?})
    (useHotkeys "g>a" (navigate-shortcut "g>a" :analytics-models) #js {:enabled analytics-models?})
    (useHotkeys "g>w" (navigate-shortcut "g>w" :warehouse-usage) #js {:enabled warehouse-usage?})

    [:f> ui.modal/modal {:id modal-id

                         :initial-props {:height 720}

                         :header-props {:title "Keyboard shortcuts"}

                         :open? open?
                         :on-close #(dispatch [:modals.events/toggle-modal modal-id false])}

     [:div {:class "grow min-h-0 overflow-y-auto p-6 pb-2 bg-kleene-background"
            :tabIndex -1}
      [:div {:class "flex flex-row gap-4"}
       [:div {:class "w-1/2"}

        [:div {:class "flex flex-col gap-4 w-full"}
         [:div {:class "border border-kleene-border rounded-md w-full"}
          [:header {:class "px-4 py-2 border-b border-kleene-border"}
           [typography/label "Global"]]

          [:ul {:class "list-none divide-y divide-solid"}
           [shortcut "Toggle console modal" ["cmd" "k"]]
           [shortcut "Show shortcuts (this window)" ["?"]]]]

         [:div {:class "border border-kleene-border rounded-md w-full"}
          [:header {:class "px-4 py-2 border-b border-kleene-border"}
           [typography/label "Navigation"]]

          [:ul {:class "list-none divide-y divide-solid"}
           [shortcut "Go to Transforms" ["g" "t"] LayoutList]
           [shortcut "Go to Sources" ["g" "s"] Cable]
           [shortcut "Go to Pipelines" ["g" "p"] Share2]
           [shortcut "Go to Logs" ["g" "l"] ListChecks]
           [shortcut "Go to Transform queue" ["g" "q"] SquareStack]
           [shortcut "Go to Share data" ["g" "d"] Group]
           (when overview?
             [shortcut "Go to Overview" ["g" "o"] LayoutDashboardIcon])
           (when pipeline-editor?
             [shortcut "Go to Pipeline editor" ["g" "e"] GitGraph])
           (when analytics-models?
             [shortcut "Go to Analytics models" ["g" "a"] BrainCircuit])
           (when warehouse-usage?
             [shortcut "Go to Warehouse usage" ["g" "w"] Warehouse])]]]]

       [:div {:class "w-1/2"}

        [:div {:class "flex flex-col gap-4 w-full"}
         [:div {:class "border border-kleene-border rounded-md w-full"}
          [:header {:class "px-4 py-2 border-b border-kleene-border"}
           [typography/label "SQL console"]]
          [:ul {:class "list-none divide-y divide-solid"}
           [shortcut "Toggle sidebar" ["cmd" "i"]]
           [shortcut "Save or create new transform" ["cmd" "s"]]]]

         [:div {:class "border border-kleene-border rounded-md w-full"}
          [:header {:class "px-4 py-2 border-b border-kleene-border"}
           [typography/label "SQL console (while editing code)"]]
          [:ul {:class "list-none divide-y divide-solid"}
           [shortcut "Close SQL console" ["cmd" "k" "cmd" "k"]]
           [shortcut "Run selection" ["cmd" "Enter"]]
           [shortcut "Open command palette" ["F1"]]]]]]]]]))
